<template>
    <page-card :header="$t('pages.search.title')">
        <b-overlay :show="queryInProgress" rounded="lg" opacity="0.6">
            <!-- this overlay shall be displayed above the input while a query is being executed -->
            <template #overlay>
                <div class="d-flex align-items-center">
                    <b-spinner small type="grow" variant="secondary"></b-spinner>
                    <b-spinner type="grow" variant="dark"></b-spinner>
                    <b-spinner small type="grow" variant="secondary"></b-spinner>
                    <span class="sr-only">{{ $t('common.please_wait') }}</span>
                </div>
            </template>
            <!-- the actual form -->
            <b-form @submit.prevent="performSearch()">
                <b-input-group size="sm" class="mb-1">
                    <b-input-group-prepend class="d-none d-sm-flex">
                        <b-input-group-text id="query-label">
                            {{ $t('pages.search.your_query') }}
                        </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input v-model="queryInput" id="query-in-modal"
                                  aria-labelledby="query-label" :placeholder="$t('pages.search.placeholder')">
                    </b-form-input>
                    <b-input-group-append>
                        <b-button type="submit">
                            <b-icon-search aria-hidden="true"/>
                            {{ $t('pages.search.perform_search') }}
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-form>
        </b-overlay>

        <!-- main content showing tabs for each result category-->
        <b-card no-body v-if="queryInput.length && results">
            <b-tabs v-if="!queryInProgress" card fill v-model="activeTab">
                <!-- one tab for each set of results -->
                <b-tab v-for="(resultSet, key) in results" :key="key">
                    <!-- title of the tab -->
                    <template #title>
                        <b-icon :icon="icons[key] ? icons[key] : 'search'" aria-hidden="true"/>
                        {{ $t('pages.search.backend_' + key) }}
                        {{ $tc('pages.search.hits', resultCounts[key], {count: resultCounts[key]}) }}
                    </template>

                    <!-- tab content (list of results) -->
                    <b-list-group v-if="resultCounts[key]" class="mt-1" flush>
                        <b-list-group-item v-for="(result, index) of resultSet" :key="index" tabindex="0">
                            <div class="d-flex w-100 justify-content-between">
                                <div class="mb-1 h5">
                                    <a :href="result.data.url">
                                        {{ result.title }}
                                        <b-icon-link45deg aria-hidden="true"/>
                                    </a>
                                </div>
                            </div>

                            <div v-html="$purify.sanitize(result.content)">

                            </div>
                        </b-list-group-item>
                    </b-list-group>

                    <callout v-else>
                        {{ $t('pages.search.no_results_for', {query: queryCurrent}) }}
                    </callout>
                </b-tab>
            </b-tabs>
        </b-card>

        <callout v-else>
            {{ $t('pages.search.no_query') }}
        </callout>
    </page-card>
</template>

<script>
import PageCard from "../layout/PageCard";
import Callout from "../layout/Callout";
import VueRouter from 'vue-router'
import Bologna from "../../Bologna";

const {isNavigationFailure, NavigationFailureType} = VueRouter
export default {
    name: "MultiSearch",
    components: {PageCard, Callout},
    props: {
        url: {
            type: String,
            required: true
        },
        initialQuery: {
            type: String,
            required: false,
            default: ''
        }
    },
    data: () => {
        return {
            queryInput: '',
            queryCurrent: '',
            queryInProgress: false,
            results: {},
            resultCounts: {},
            activeTab: 0,
            icons: {
                'ldap': 'person',
                'software': 'disc',
                'services': 'grid',
                'ur': 'globe'
            },
            purifyProfile: {USE_PROFILES: {html: false, svg: false, mathMl: false}}
        }
    },
    mounted() {
        if (this.initialQuery) {
            // a query param is set via URL
            this.queryInput = this.$purify.sanitize(this.initialQuery, this.purifyProfile);

            let previous_search_json = localStorage.getItem('previous_search');
            if (previous_search_json) {
                // there also is a previous response we have stored
                let previous_search = JSON.parse(previous_search_json);

                let previous_query = this.$purify.sanitize(previous_search.query, {
                    USE_PROFILES: {
                        html: false,
                        svg: false,
                        mathMl: false
                    }
                });
                let previous_results = previous_search.results;
                this.queryCurrent = previous_query;

                if (previous_query === this.initialQuery) {
                    // if the previous response and the initial query string match show the results again
                    this.processResults(previous_results);
                    // also restore previous active tab
                    this.restoreActiveTab();
                } else {
                    // else perform a new query with the different query string
                    this.performSearch();
                }
            } else {
                // there is no previous response, so perform the query again as the data might have been deleted somehow
                this.performSearch();
            }
        }
    },
    methods: {
        performSearch: async function () {
            // don't bother submitting a request if there is no input
            if (!this.queryInput.length) {
                return;
            }
            this.queryCurrent = this.$purify.sanitize(this.queryInput, this.purifyProfile);

            try {
                // manipulate current route so a user can navigate back to this query
                await this.$router.replace('/i/search?query=' + this.queryCurrent);
            } catch (e) {
                // don't care for errors that are due to the same request being made again, that's okay in this case
                if (!isNavigationFailure(e, NavigationFailureType.duplicated)) {
                    console.log(`Error changing URL: ${e}`)
                }
            }

            this.startSpinner();
            try {
                let response = await this.queryBackend(this.queryCurrent, this.url);
                this.processResults(response.data.data.results);
                // store response so we can restore it later
                this.storeResponse(this.queryCurrent, response.data.data.results);
            } catch (e) {
                console.log(`Failed querying backend for '${this.queryCurrent}. Error: ${e}`)
            }
            this.stopSpinner();
        },
        queryBackend: async function (query, url) {
            return this.axios.post(url, {q: query});
        },
        processResults: function (results) {
            this.results = results;
            try {
                for (let resultSet in this.results) {
                    // read out number of results per category for easier access
                    this.resultCounts[resultSet] = Object.keys(this.results[resultSet]).length;
                }
            } catch (e) {
                console.log(`Could not process search results: ${e}`)
            }
        },
        storeResponse: function (query, results) {
            // store query and response in localStorage as string
            localStorage.setItem('previous_search', JSON.stringify({query, results}))
        },
        storeActiveTab: function (value) {
            localStorage.setItem('search_active_tab', value.toString());
        },
        restoreActiveTab: function () {
            this.activeTab = parseInt(localStorage.getItem('search_active_tab')) || 0;
        },
        startSpinner: function () {
            this.queryInProgress = true;
        },
        stopSpinner: function () {
            this.queryInProgress = false;
        }
    },
    watch: {
        activeTab: function (value) {
            this.storeActiveTab(value)
        }
    }
}
</script>

<style scoped>

</style>
